import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../../lib/api'
import { getConfig, trackEvent } from '../../lib/config'
import { hcCurrency } from '../../lib/currency'
import { ButtonAnchor, ButtonSize, ButtonVariant, Hcaptcha, HcaptchaHandle, SubmitButton } from '../../components/common'
import { CotizatorSlider } from './CotizatorSlider'
import { scrollToAnchor, scrollToTop } from './scroll'
import macbookImg from '../../assets/img/hero-slider/macbook.png'
import multiplatformImg from '../../assets/img/features/multiplatform-colors.png'
import lockImg from '../../assets/img/features/lock.png'
import feature3Img from '../../assets/img/features/feature3.png'
import featureRecordImg from '../../assets/img/features/1.png'
import featureCalendarImg from '../../assets/img/features/2.png'
import featurePrescriptionImg from '../../assets/img/features/3.png'
import faqIconImg from '../../assets/img/faq-icon.png'

const SIGN_IN_PATH = '/professionals/sign_in'
const SIGN_UP_PATH = '/professionals/sign_up'

const MENU_SECTIONS = ['why', 'features', 'pricing', 'faq', 'contacts'] as const

const FEATURE_TABS = [
  { id: 'record', target: 'clean-code', label: 'Ficha Médica' },
  { id: 'prescription', target: 'font-icons', label: 'Prescripciones' },
  { id: 'calendar', target: 'more', label: 'Agenda' },
] as const

type Alert = { kind: 'success' | 'error'; message: string }

function ScrollLink({
  hash,
  className = 'scroll',
  variant,
  children,
  id,
}: {
  hash: string
  className?: string
  variant?: ButtonVariant
  children: ReactNode
  id?: string
}) {
  const scroll = (event: React.MouseEvent) => {
    event.preventDefault()
    trackEvent('Landing Section Clicked', { section: hash.replace('#', '') })
    scrollToAnchor(hash)
  }

  if (variant) {
    return (
      <ButtonAnchor id={id} variant={variant} className={className} href={hash} onClick={scroll}>
        {children}
      </ButtonAnchor>
    )
  }

  return (
    <a id={id} className={className} href={hash} onClick={scroll}>
      {children}
    </a>
  )
}

/** The sign-up and sign-in anchors, which leave the SPA, so the event goes by beacon. */
function CtaLink({
  cta,
  placement,
  className,
  variant,
  size,
  children,
}: {
  cta: 'sign_up' | 'sign_in'
  placement: 'header' | 'mobile_menu' | 'hero'
  className?: string
  variant?: ButtonVariant
  size?: ButtonSize
  children: ReactNode
}) {
  const href = cta === 'sign_up' ? SIGN_UP_PATH : SIGN_IN_PATH
  const track = () => trackEvent('Landing CTA Clicked', { cta, placement }, { transport: 'sendBeacon' })

  if (variant) {
    return (
      <ButtonAnchor href={href} variant={variant} size={size} className={className} onClick={track}>
        {children}
      </ButtonAnchor>
    )
  }

  return (
    <a href={href} className={className} onClick={track}>
      {children}
    </a>
  )
}

function FlashAlerts({ alerts, onDismiss }: { alerts: Alert[]; onDismiss: (index: number) => void }) {
  if (alerts.length === 0) {
    return null
  }
  return (
    <div id="error_explanation">
      <ul style={{ paddingLeft: 0 }}>
        {alerts.map((alert, index) => (
          <div key={index} className={`alert alert-theme alert-${alert.kind === 'success' ? 'success' : 'danger'} alert-dismissible`} role="alert">
            <button
              type="button"
              className="close"
              aria-label="Close"
              style={{ marginRight: 20, fontSize: 22 }}
              onClick={() => onDismiss(index)}
            >
              <span aria-hidden="true">×</span>
            </button>
            {alert.kind === 'success' ? alert.message : <p>{alert.message}</p>}
          </div>
        ))}
      </ul>
    </div>
  )
}

function Header({ activeSection }: { activeSection: string }) {
  const [menuOpen, setMenuOpen] = useState(false)
  return (
    <header className="stiky-header">
      <div className="container group">
        <a href="/landing" className="logo">
          <img src="/navbar_icon.png" alt="Historia Clínica" style={{ paddingBottom: 15 }} />
        </a>
        <a href="/landing" className="logo-mobile">
          <img src="/navbar_icon.png" alt="Historia Clínica" />
        </a>
        <div className="navi-toggle group" onClick={() => setMenuOpen((open) => !open)}>
          <span className="dot" />
          <span className="line" />
          <span className="dot" />
          <span className="line" />
          <span className="dot" />
          <span className="line" />
          <span className="dot" />
          <span className="line" />
        </div>
        <CtaLink cta="sign_up" placement="header" variant="primary" size="sm" className="hidden-xs btn-login">
          <i className="icon-user" />
          Cuenta Gratis
        </CtaLink>
        <CtaLink cta="sign_in" placement="header" className="alone-menu-element hidden-xs">
          Iniciar Sesión
        </CtaLink>
        <nav className={`main-navi${menuOpen ? ' open' : ''}`}>
          <ul>
            <li className={`menu-element${activeSection === 'why' ? ' active' : ''}`}>
              <ScrollLink hash="#why">¿Por qué HC?</ScrollLink>
            </li>
            <li className={`menu-element${activeSection === 'features' ? ' active' : ''}`}>
              <ScrollLink hash="#features">Características</ScrollLink>
            </li>
            <li className={`menu-element${activeSection === 'pricing' ? ' active' : ''}`}>
              <ScrollLink hash="#pricing">Planes</ScrollLink>
            </li>
            <li className={`menu-element hidden-md${activeSection === 'faq' ? ' active' : ''}`}>
              <ScrollLink hash="#faq">FAQ</ScrollLink>
            </li>
            <li className={`menu-element${activeSection === 'contacts' ? ' active' : ''}`}>
              <ScrollLink hash="#contacts">Contacto</ScrollLink>
            </li>
            <li>
              <CtaLink cta="sign_in" placement="mobile_menu" className="skip-menu-select visible-xs">
                Iniciar Sesión
              </CtaLink>
            </li>
            <li>
              <CtaLink cta="sign_up" placement="mobile_menu" className="skip-menu-select visible-xs">
                Cuenta Gratis
              </CtaLink>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section className="hero">
      <div className="container">
        <div className="row" style={{ marginTop: 100, paddingBottom: 40 }}>
          <div className="hero-slider">
            <div className="slide first-slide">
              <div className="col-lg-5 col-md-4 col-sm-12 animated fadeInDown">
                <h2 style={{ marginTop: 70 }} className="text-light text-left bold">
                  Historia Clínica
                </h2>
                <p className="text-left">Software 100% online</p>
                <p className="text-left">
                  Lleva el registro de tus pacientes y fichas clínicas de forma <b>fácil, segura y desde dónde quieras.</b>
                </p>
                <p className="text-left">Olvídate del papel y lleva la tecnología en administración a tu clínica o consulta.</p>
                <div className="col-xs-12">
                  <CtaLink cta="sign_up" placement="hero" variant="primary">
                    Cuenta Gratis
                  </CtaLink>
                  <ScrollLink hash="#cotizator" variant="primary" className="scroll">
                    Cotizar Plan
                  </ScrollLink>
                </div>
              </div>
              <div className="col-lg-7 col-md-8 col-sm-12 animated fadeInRight">
                <div className="video">
                  <img src={macbookImg} width={782} height={422} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function Features() {
  const [activeTab, setActiveTab] = useState<string>('clean-code')

  useEffect(() => {
    const tabParam = new URLSearchParams(window.location.search).get('tab')
    const tab = FEATURE_TABS.find((candidate) => candidate.id === tabParam)
    if (tab) {
      setActiveTab(tab.target)
    }
  }, [])

  return (
    <section className="page-block" id="why">
      <div className="container">
        <div className="row page-header" style={{ marginBottom: 30 }}>
          <h2>¿Por qué Historia Clínica?</h2>
          <span style={{ textAlign: 'justify' }}>
            Llevar registros en papel es desordenado, las hojas se pierden y a largo plazo es costoso mantener almacenados los registros en
            buen estado.
          </span>
        </div>
        <div className="row features">
          <div className="col-lg-4 col-md-4 col-sm-4">
            <div className="feature-img" style={{ width: 122 }}>
              <img src={multiplatformImg} width={124} height={120} alt="Editable colors" />
            </div>
            <h3>Multiplataforma</h3>
            <p>Puedes acceder a Historia Clínica desde tu computador, celular o tablet</p>
          </div>
          <div className="col-lg-4 col-md-4 col-sm-4">
            <div className="feature-img" style={{ width: 95 }}>
              <img src={lockImg} width={131} height={131} alt="All you need for start" />
            </div>
            <h3>Seguro</h3>
            <p>Sabemos que la seguridad es importante, por lo que se hacen copias de tus registros todos los dias</p>
          </div>
          <div className="col-lg-4 col-md-4 col-sm-4">
            <div className="feature-img">
              <img src={feature3Img} width={72} height={72} alt="Clean code" />
            </div>
            <h3>Fácil de usar</h3>
            <p>Historia Clínica es una herramienta potente y práctica. Puedes empezar a usarla en solo minutos</p>
          </div>
        </div>
      </div>
      <div className="container-fluid" id="features" style={{ paddingTop: 50 }}>
        <div className="row">
          <ul className="nav nav-tabs central">
            {FEATURE_TABS.map((tab) => (
              <li key={tab.id} className={activeTab === tab.target ? 'active' : ''}>
                <a
                  href={`#${tab.target}`}
                  id={tab.id}
                  onClick={(event) => {
                    event.preventDefault()
                    trackEvent('Landing Feature Tab Switched', { tab: tab.id })
                    setActiveTab(tab.target)
                  }}
                >
                  {tab.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="container">
        <div className="row tab-content central">
          <div className={`tab-pane fade col-lg-12${activeTab === 'clean-code' ? ' in active' : ''}`} id="clean-code">
            <div className="row">
              <div className="col-lg-6 col-md-6 animated fadeInLeft">
                <img src={featureRecordImg} alt="Clean Code" />
              </div>
              <div className="col-lg-6 col-md-6 animated fadeInRight">
                <div className="row">
                  <div className="col-lg-12">
                    <h2>Ficha Médica</h2>
                    <p className="double-space-bottom">Mantén las fichas de tus pacientes en un solo lugar, de forma segura y ordenada.</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-2">
                    <i className="feature-icon icon-clock" />
                  </div>
                  <div className="col-lg-10">
                    <p className="double-space-bottom">
                      No pierdas tu tiempo con soluciones complejas. Empieza a usar Historia Clínica en solo minutos
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-2">
                    <i className="feature-icon icon-profile" />
                  </div>
                  <div className="col-lg-10">
                    <p className="double-space-bottom">
                      Porque no todos tienen las mismas necesidades, tenemos una ficha médica distinta para cada especialidad
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-2">
                    <i className="feature-icon icon-attachment" />
                  </div>
                  <div className="col-lg-10">
                    <p className="double-space-bottom">Adjunta documentos y exámenes de tus pacientes para llevar su seguimiento</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`tab-pane fade col-lg-12${activeTab === 'font-icons' ? ' in active' : ''}`} id="font-icons">
            <div className="row">
              <div className="col-lg-6 col-md-6 animated fadeInLeft">
                <img className="img-center" src={featurePrescriptionImg} alt="More Features" />
              </div>
              <div className="col-lg-6 col-md-6 animated fadeInRight">
                <div className="row">
                  <div className="col-lg-12">
                    <h2>Prescripciones</h2>
                    <p className="double-space-bottom">
                      Administra las recetas, exámenes e indicaciones de tus pacientes con nuestro módulo de prescripciones
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-2">
                    <i className="feature-icon icon-cloud-download" />
                  </div>
                  <div className="col-lg-10">
                    <p className="double-space-bottom">Imprime cualquiera de estos documentos directamente desde la plataforma</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-2">
                    <i className="feature-icon icon-history" />
                  </div>
                  <div className="col-lg-10">
                    <p>Revisa el historial de las prescripciones emitidas a tus pacientes en cualquier momento</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`tab-pane fade col-lg-12${activeTab === 'more' ? ' in active' : ''}`} id="more">
            <div className="row">
              <div className="col-lg-6 col-md-6 animated fadeInLeft">
                <img src={featureCalendarImg} alt="Font Icons" />
              </div>
              <div className="col-lg-6 col-md-6 animated fadeInRight">
                <div className="row">
                  <div className="col-lg-12">
                    <h2>Agenda</h2>
                    <p className="double-space-bottom">Organiza tu tiempo con nuestro módulo de calendario</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-2">
                    <i className="feature-icon icon-users" />
                  </div>
                  <div className="col-lg-10">
                    <p className="double-space-bottom">
                      Cada profesional tiene su propio horario e Historia Clínica lo considera para mostrarte las horas disponibles
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-2">
                    <i className="feature-icon icon-calendar" />
                  </div>
                  <div className="col-lg-10">
                    <p className="double-space-bottom">
                      Agenda las citas de tus pacientes según sus necesidades, en una fecha específica o con un profesional determinado
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-2">
                    <i className="feature-icon icon-cog" />
                  </div>
                  <div className="col-lg-10">
                    <p>Gestiona múltiples box de atención, cada uno con su propio calendario</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function Pricing({ monthlyDefaultPrice }: { monthlyDefaultPrice: number }) {
  return (
    <section className="page-block" id="pricing">
      <div className="container">
        <div className="row page-header">
          <h2>Planes</h2>
          <span>
            Estos precios son netos y de pago mensual con emisión de factura electrónica.
            <br />
            Si tienes cualquier duda, no dudes en <ScrollLink hash="#contacts">contactarnos.</ScrollLink>
          </span>
        </div>
        <div className="row">
          <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12">
            <div className="pricing-plan highlited">
              <div className="header">
                <h3>Plan Personal</h3>
                <span className="price"> GRATIS</span>
              </div>
              <div className="body">
                <ul className="pricing-list">
                  <li>
                    1 cuenta de profesional
                    <br />
                    <br />
                  </li>
                  <li>Hasta 20 pacientes</li>
                  <li>Ficha estándar según profesiones disponibles</li>
                  <li>
                    Almacenamiento de archivos
                    <p className="tiny-price">(2 GB)</p>
                  </li>
                  <li>
                    Impresión de prescripciones
                    <p className="tiny-price"> (1 mes de prueba) </p>
                  </li>
                  <li>
                    Agenda online
                    <p className="tiny-price"> (1 mes de prueba) </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12">
            <div className="pricing-plan highlited">
              <div className="header">
                <h3>Plan Personal Premium</h3>
                <span className="price"> {hcCurrency(monthlyDefaultPrice)}</span>
              </div>
              <div className="body">
                <ul className="pricing-list">
                  <li style={{ textAlign: 'center' }}>
                    1 cuenta de profesional <br /> 1 cuenta de administración
                  </li>
                  <li>Pacientes ilimitados</li>
                  <li>
                    Ficha estándar según tu profesión
                    <br />
                    <br />
                  </li>
                  <li>
                    Almacenamiento de archivos
                    <p className="tiny-price">(25 GB)</p>
                  </li>
                  <li>
                    Impresión de prescripciones
                    <p className="tiny-price" />
                    <br />
                  </li>
                  <li>
                    Agenda online
                    <br />
                    <br />
                    <p className="tiny-price" />
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12">
            <div className="pricing-plan highlited">
              <div className="header">
                <h3>Plan Centro Médico</h3>
                <span className="price">
                  {' '}
                  <ScrollLink hash="#cotizator">Cotizar</ScrollLink>
                </span>
              </div>
              <div className="body">
                <ul className="pricing-list">
                  <li>Nº de cuentas profesionales y administrativas según requerimientos</li>
                  <li>Pacientes ilimitados</li>
                  <li>Ficha personalizable según requerimiento del centro médico</li>
                  <li>
                    Almacenamiento de archivos
                    <p className="tiny-price">(Ilimitado)</p>
                  </li>
                  <li>
                    Impresión de prescripciones
                    <p className="tiny-price" />
                  </li>
                  <li>
                    Agenda online
                    <p className="tiny-price" />
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="row" style={{ fontSize: '1.7em', fontWeight: 300 }}>
          <br />
          <p>Todos los planes poseen además las siguientes características:</p>
          <ul>
            <li>Número de sesiones ilimitadas por paciente</li>
            <li>Soporte técnico</li>
            <li>Seguridad de acceso</li>
            <li>Respaldo de la información todos los días</li>
            <li>Actualizaciones automáticas y sin instalación</li>
            <li>Acceso ilimitado al sistema y desde cualquier dispositivo con Internet</li>
          </ul>
        </div>
      </div>
    </section>
  )
}

function getCotizatorText(professionals: number): string {
  if (professionals === 1) {
    return '1 profesional'
  }
  if (professionals < 21) {
    return `${professionals} profesionales`
  }
  return 'Más de 20 profesionales'
}

function getCotizatorPrice(professionals: number, discount: number): string | null {
  let value: number
  if (professionals < 6) {
    value = 40
  } else if (professionals < 11) {
    value = 60
  } else if (professionals < 16) {
    value = 75
  } else if (professionals < 21) {
    value = 80
  } else {
    return null
  }
  return `${hcCurrency(Math.round(value * discount * 1000))} / Mes`
}

function CotizatorSection() {
  const [professionals, setProfessionals] = useState(2)
  const [discount, setDiscount] = useState(1)
  const price = getCotizatorPrice(professionals, discount)

  const configure = (nextProfessionals: number, nextDiscount: number) => {
    trackEvent('Quote Configured', {
      professionals: nextProfessionals,
      billing_period: nextDiscount === 1 ? 'monthly' : 'annual',
      monthly_price: getCotizatorPrice(nextProfessionals, nextDiscount),
    })
  }

  return (
    <section className="page-block contacts" id="cotizator">
      <div className="container">
        <div className="row page-header">
          <h2>Cotizador</h2>
          <span>Tenemos un plan para ti</span>
          <br />
          <br />
          <span>Selecciona el número de profesionales que usarán Historia Clínica en tu centro médico o consulta y cotiza en tiempo real</span>
        </div>
        <div className="row">
          <div className="col-md-5 col-md-offset-1">
            <br />
            <br />
            <div className="row">
              <div className="" style={{ marginBottom: 10 }}>
                Mueve la barra para cambiar el número de profesionales:
              </div>
              <div className="col-sm-offset-1 col-sm-8 col-xs-offset-1 col-xs-10 " style={{ padding: 10 }}>
                <div className="title text-center slider-handle-span">{getCotizatorText(professionals)}</div>
                <div className="holder">
                  <div className="demo">
                    <CotizatorSlider
                      onChange={setProfessionals}
                      onCommit={(next) => configure(next, discount)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <br />
            <br />
            <div className="row">
              <div className="title" style={{ marginBottom: 10 }}>
                Tienes descuento si contratas anualmente
              </div>
              <div className="col-xs-offset-1" style={{ paddingLeft: 5, textAlign: 'left' }}>
                <div className="cotizator-option">
                  <input type="radio" id="pay1" name="type_pay" value="1" checked={discount === 1} onChange={() => { setDiscount(1); configure(professionals, 1) }} />
                  <label className="label-iCheck-helper" style={{ fontWeight: 'normal' }} htmlFor="pay1">
                    Mensual
                  </label>
                  <br />
                </div>
                <div className="cotizator-option">
                  <input type="radio" id="pay2" name="type_pay" value="0.9" checked={discount === 0.9} onChange={() => { setDiscount(0.9); configure(professionals, 0.9) }} />
                  <label className="label-iCheck-helper" style={{ fontWeight: 'normal' }} htmlFor="pay2">
                    Anual (- 10%)
                  </label>
                </div>
              </div>
            </div>
            <br />
            <br />
            <div className="row">
              <div className="title">
                Si tienes alguna duda o te interesa algún plan, por favor{' '}
                <ScrollLink hash="#contacts" id="contact-button">
                  contáctanos
                </ScrollLink>
              </div>
              <br />
            </div>
          </div>
          <div className="col-md-5">
            <div className="pricing-plan highlited">
              <div className="header">
                <h3>
                  Plan para <div className="slider-handle-span">{getCotizatorText(professionals)}</div>
                </h3>
                <span className="price slider-handle-span-price">
                  {price ?? <ScrollLink hash="#contacts">Contáctanos</ScrollLink>}
                </span>
              </div>
              <div className="body">
                <ul>
                  <li>Pacientes ilimitados</li>
                  <li>Impresión de prescripciones</li>
                  <li>Agenda online</li>
                  <li>Todas las ventajas de una cuenta premium</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const FAQ_ITEMS: { question: string; answer: ReactNode }[] = [
  {
    question: '¿Cómo consigo una cuenta?',
    answer: (
      <>
        <p>Envianos un correo a contacto@historiaclinica.cl con el plan que te interesa, y crearemos tu cuenta.</p>
        <p>
          También puedes <ScrollLink hash="#contacts">contactarnos desde aquí.</ScrollLink>
        </p>
      </>
    ),
  },
  {
    question: '¿La cuenta gratuita es por tiempo ilimitado?',
    answer: <p>Si, puedes acceder al sistema de forma indefinida.</p>,
  },
  {
    question: 'Necesito el software para mi centro médico, ¿qué hago?',
    answer: (
      <>
        <p>Envianos un correo a contacto@historiaclinica.cl para organizar una reunión y analizar cuales son las necesidades del centro.</p>
        <p>
          También puedes <ScrollLink hash="#contacts">contactarnos desde aquí.</ScrollLink>
        </p>
      </>
    ),
  },
  {
    question: '¿Quién tiene acceso a la información de mis pacientes?',
    answer: (
      <>
        <p>La información de los pacientes es privada, sólamente el dueño de la cuenta puede acceder a la información.</p>
        <p>En un centro médico, la ficha clínica es compartida por los profesionales que atienden al paciente.</p>
      </>
    ),
  },
  {
    question: 'Reglamentación',
    answer: (
      <>
        <p>Reglamentación para el funcionamiento del software Historia Clínica</p>
        <p>
          1. El software Historia Clínica es administrado por la sociedad Servicios Computacionales Clinicos SpA, desde ahora SCC, quien
          vela por la preservación de la estabilidad, la seguridad y la interoperabilidad del sistema.
        </p>
        <p>2. SCC mantendrá un servicio de información web en:</p>
        <p>http://www.historiaclinica.cl</p>
        <p>
          La información publicada en ese lugar se entenderá conocida por todos los usuarios de Historia Clinica, y SCC no estará obligado a
          realizar ningún otro tipo de publicación.
        </p>
        <p>
          3. El software Historia Clínica se rige por el decreto Nº 41 reglamento ley 20.584 sobre Fichas Clínicas. SCC tiene el deber de
          que el software provea las funcionalidades necesarias para cumplir con la ley vigente sobre Fichas Clínicas.
        </p>
        <p>
          4. SCC mantendrá un servicio de soporte. Este servicio estará disponible todos los días hábiles en horario de 09:00 a 20:00. La
          vía de acceso a este servicio es:
        </p>
        <p>a.- Email: contacto@historiaclinica.cl</p>
        <p>b.- El usuario al percatarse de un problema o inconveniente en el sistema deberá proceder de la siguiente manera:</p>
        <p>
          b.1.- Comunicarse con el área de soporte de Historia Clínica, a través del email indicado entregando los antecedentes necesarios
          para investigar el problema.
        </p>
        <p>b.2.- El área de soporte de Historia Clínica se comunicará con la contraparte técnica del cliente para recaudar más información.</p>
        <p>b.3.- El área de soporte de Historia Clínica debe solucionar el problema.</p>
        <p>b.4.- Una vez resuelto el problema, el área de soporte debe informar al usuario final que reportó el error.</p>
        <p>
          b.5.- El usuario final verifica la solución. Si la solución es satisfactoria se da por cerrada la contingencia, en caso contrario,
          se deben informar los inconvenientes para solucionar el problema.
        </p>
        <p>5. SCC estará facultado para cobrar una tarifa mensual por los servicios que presta y publicará una tabla de tarifas vigentes.</p>
        <p>Será requisito esencial para mantener la vigencia de la cuenta en Historia Clinica el que se pague oportunamente todas las tarifas que sean aplicables.</p>
        <p>
          SCC proveerá distintos medios para el pago de la tarifa. La imposibilidad de utilizar alguno de dichos mecanismos no será excusa
          para que el solicitante no cumpla con su obligación de pagar, pudiendo optar por las otras alternativas de medios de pago
          disponibles.
        </p>
        <p>Reglamentación para el uso del software Historia Clínica</p>
        <p>6. Se entenderá que toda persona por el hecho de ser titular de una cuenta en Historia Clínica:</p>
        <p>a.- Declara estar en conocimiento, haber leído y aceptado la Reglamentación para el Funcionamiento y Uso del Software Historia Cínica.</p>
        <p>
          b.- Acepta que los datos del registro sean informados a requerimiento formal de cualquier autoridad judicial o administrativa
          facultada legalmente para requerirla.
        </p>
        <p>
          c.- Declara que, a su leal saber y entender, el uso del software de Historia Clínica se realiza con fines lícitos, de buena fe y
          que no infringe ni viola de ninguna manera cualquier derecho de un tercero.
        </p>
        <p>d.- Declara que los datos que entrega son completos y exactos, obligándose a mantener la información permanentemente actualizada.</p>
        <p>7. Podrá ser titular de una cuenta en el software Historia Clínica toda persona natural o jurídica, nacional o extranjera.</p>
        <p>Excepcionalmente, SCC se reserva la facultad de pedir información que compruebe la identidad del titular, cuando sea necesario.</p>
        <p>Solo el titular de la cuenta será el contacto administrativo, el cual tendrá la calidad de representante para todos los efectos reglamentarios.</p>
        <p>8. Las solicitudes de inscripción se recibirán por vía presencial o electrónica, a través de los mecanismos que SCC defina para tales efectos.</p>
        <p>9. El titula de la cuenta debe llevar un registro local de la información usando alguno de los medios que el software Historia Clínica provee.</p>
        <p>
          10. El titular de la cuenta es responsable de utilizar el software de tal forma que no inflinja ni viole de ninguna manera el
          reglamento sobre Fichas Clínicas vigente.
        </p>
      </>
    ),
  },
  {
    question: '¿En base a que ley fue creado el software?',
    answer: <p>Decreto Nº 41 Reglamento ley 20.584 sobre Fichas Clínicas.</p>,
  },
]

function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  return (
    <section className="page-block faq" id="faq">
      <div className="container">
        <div className="row page-header">
          <h2>Preguntas Frecuentes</h2>
          <span />
        </div>
        <div className="row">
          <div className="col-lg-3 col-md-4 col-sm-4">
            <img src={faqIconImg} className="img-center" alt="FAQ" />
          </div>
          <div className="col-lg-8 col-md-8 col-sm-8">
            <div className="panel-group" id="accordion-faq">
              {FAQ_ITEMS.map((item, index) => (
                <div className="panel" key={index}>
                  <a
                    className={`panel-heading${openIndex === index ? '' : ' collapsed'}`}
                    href={`#faq-collapse-${index}`}
                    onClick={(event) => {
                      event.preventDefault()
                      if (openIndex !== index) {
                        trackEvent('FAQ Opened', { question: index, question_text: item.question })
                      }
                      setOpenIndex(openIndex === index ? null : index)
                    }}
                  >
                    {item.question}
                    <div className="indicator">
                      <div className="icon">
                        <span className="hr-line" />
                        <span className="vr-line" />
                      </div>
                    </div>
                  </a>
                  <div id={`faq-collapse-${index}`} className={`panel-collapse collapse${openIndex === index ? ' in' : ''}`}>
                    <div className="panel-body">{item.answer}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function ContactSection({ onResult }: { onResult: (alert: Alert) => void }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', profession: '', comments: '' })
  const [submitting, setSubmitting] = useState(false)
  const captchaRef = useRef<HcaptchaHandle | null>(null)
  const config = getConfig()

  const setField = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [field]: event.target.value })

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting) {
      return
    }
    setSubmitting(true)
    try {
      const result = await api.post<{ success?: string; error?: string }>('/contacts', {
        contact: form,
        'h-captcha-response': captchaRef.current?.getResponse() ?? '',
      })
      // The handler has two failure shapes — a resolve carrying `error` and a
      // reject — so both branches report the same event.
      trackEvent('Contact Form Submitted', { success: Boolean(result.success) })
      if (result.success) {
        onResult({ kind: 'success', message: result.success })
        setForm({ name: '', email: '', phone: '', profession: '', comments: '' })
      } else {
        onResult({ kind: 'error', message: result.error ?? 'Ingresa una dirección de email válida' })
      }
      captchaRef.current?.reset()
    } catch (error) {
      const body = error instanceof ApiError ? (error.body as { error?: string } | null) : null
      trackEvent('Contact Form Submitted', { success: false })
      onResult({ kind: 'error', message: body?.error ?? 'Ocurrió un error, inténtalo nuevamente' })
      captchaRef.current?.reset()
    } finally {
      setSubmitting(false)
      scrollToTop()
    }
  }

  return (
    <section className="page-block contacts" id="contacts">
      <div className="container">
        <div className="row page-header">
          <h2>Contacto</h2>
          <span style={{ fontSize: '1.5em' }}>Teléfono / Whatsapp (+56) 9 66860733</span>
          <br />
          <span style={{ fontSize: '1.5em' }}>Envía tus comentarios y con gusto te ayudaremos</span>
        </div>
        <div className="row">
          <div className="col-md-6 col-md-offset-3 col-xs-10 col-xs-offset-1">
            <form onSubmit={(event) => void handleSubmit(event)}>
              <div className="form-group">
                <label htmlFor="contact_name">Nombre</label>
                <input id="contact_name" type="text" className="form-control" value={form.name} onChange={setField('name')} />
              </div>
              <div className="form-group">
                <label htmlFor="contact_email">Email</label>
                <input id="contact_email" type="text" className="form-control" value={form.email} onChange={setField('email')} />
              </div>
              <div className="form-group">
                <label htmlFor="contact_phone">Teléfono</label>
                <input id="contact_phone" type="text" className="form-control" value={form.phone} onChange={setField('phone')} />
              </div>
              <div className="form-group">
                <label htmlFor="contact_profession">Profesión</label>
                <input id="contact_profession" type="text" className="form-control" value={form.profession} onChange={setField('profession')} />
              </div>
              <div className="form-group">
                <label htmlFor="contact_comments">Comentarios</label>
                <textarea id="contact_comments" className="form-control" rows={8} value={form.comments} onChange={setField('comments')} />
              </div>
              {config.hcaptchaEnabled && <Hcaptcha theme="light" handleRef={captchaRef} form="contact" />}
              <div className="form-group">
                <SubmitButton label="Enviar" style={{ width: '100%' }} disabled={submitting} />
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="footer">
      <div className="row">
        <div className="col-lg-12">
          <p className="copyright" style={{ marginTop: 0 }}>
            <a href="https://www.facebook.com/apphistoriaclinica/">
              <i className="icon-facebook2" style={{ fontSize: 20, marginRight: 10 }} />
            </a>
            <a href="https://www.instagram.com/historiaclinica/">
              <i className="icon-instagram" style={{ fontSize: 20 }} />
            </a>
          </p>
          <p className="copyright" style={{ marginTop: 0 }}>
            (+56) 9 66860733
          </p>
          <p className="copyright" style={{ marginTop: 0 }}>
            contacto@historiaclinica.cl
          </p>
          <p className="copyright" style={{ marginTop: 0 }}>
            Un producto creado por <a href="https://www.quantic.cl">Quantic Labs</a>
          </p>
        </div>
      </div>
    </footer>
  )
}

function useActiveSection(): string {
  const [active, setActive] = useState('')
  useEffect(() => {
    const handleScroll = () => {
      const fromTop = window.scrollY + 280
      let current = ''
      for (const id of MENU_SECTIONS) {
        const element = document.getElementById(id)
        if (element && element.getBoundingClientRect().top + window.scrollY < fromTop) {
          current = id
        }
      }
      setActive(current)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  return active
}

function useScrollTopVisible(): boolean {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 500)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  return visible
}

function useScrollToHashOnLoad() {
  useEffect(() => {
    const { hash } = window.location
    if (hash && document.getElementById(hash.slice(1))) {
      scrollToAnchor(hash)
    }
  }, [])
}

export default function LandingPage() {
  const config = getConfig()
  const [alerts, setAlerts] = useState<Alert[]>(() => {
    const initial: Alert[] = []
    if (config.flash.success) {
      initial.push({ kind: 'success', message: config.flash.success })
    }
    if (config.flash.error) {
      initial.push({ kind: 'error', message: config.flash.error })
    }
    return initial
  })
  const activeSection = useActiveSection()
  const scrollTopVisible = useScrollTopVisible()
  useScrollToHashOnLoad()

  return (
    <>
      <div className="flash-message">
        <FlashAlerts alerts={alerts} onDismiss={(index) => setAlerts(alerts.filter((_, i) => i !== index))} />
      </div>
      <Header activeSection={activeSection} />
      <Hero />
      <Features />
      <Pricing monthlyDefaultPrice={config.monthlyDefaultPrice} />
      <CotizatorSection />
      <Faq />
      <ContactSection onResult={(alert) => setAlerts([alert])} />
      <Footer />
      <div
        id="scroll-top"
        className={`scroll-up${scrollTopVisible ? ' visible' : ''}`}
        onClick={(event) => {
          event.preventDefault()
          scrollToTop()
        }}
      >
        <i className="icon-arrow-up" />
      </div>
    </>
  )
}
